import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { View, PageName } from '../types';

interface ViewContextType {
  view: View;
  setView: (view: View) => void;
}

const ViewContext = createContext<ViewContextType | undefined>(undefined);

export const ViewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [view, setView] = useState<View>({ page: 'Dashboard' });
  const location = useLocation();

  // Reset view state when route changes
  useEffect(() => {
    // Map routes to page names and reset subPage
    const routeToPage: Record<string, PageName> = {
      '/admin/dashboard': 'Dashboard',
      '/admin/content': 'Content',
      '/admin/press-release': 'Press Release',
      '/admin/strategic-pillars': 'Strategic Pillars',
      '/admin/programmes': 'Programmes',
      '/admin/publications': 'Publications',
      '/admin/photo-gallery': 'Photo Gallery',
      '/admin/subscribers': 'Subscribers',
      '/admin/admin-users': 'Admin Users',
    };

    const pageName = routeToPage[location.pathname];
    if (pageName && view.page !== pageName) {
      // Reset to list view when navigating to a new page
      setView({ page: pageName });
    }
  }, [location.pathname, view.page, setView]);

  return (
    <ViewContext.Provider value={{ view, setView }}>
      {children}
    </ViewContext.Provider>
  );
};

export const useView = () => {
  const context = useContext(ViewContext);
  if (context === undefined) {
    throw new Error('useView must be used within a ViewProvider');
  }
  return context;
};